import {jwtDecode} from 'jwt-decode';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
export const TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN = 'refresh_token';
const TOKEN_PAYLOAD_KEY = 'token_payload';

export const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      error.response.data.code === 'token_not_valid'
    ) {
      logout();
      window.dispatchEvent(new Event('tokenChanged'));
      window.location.href = '/login'; // Redirect to login page
      return Promise.reject(error);
    }

    if (error.response?.status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
      if (!refreshTokenValue) {
        console.log('No refresh token, logging out');
        logout();
        window.dispatchEvent(new Event('tokenChanged'));
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshToken();
        if (newAccessToken) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          return axios(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.error('Token refresh failed:', refreshError);
        logout();
        window.dispatchEvent(new Event('tokenChanged'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function storeTokenPayload(token) {
  try {
    const decoded = jwtDecode(token);
    localStorage.setItem(TOKEN_PAYLOAD_KEY, JSON.stringify(decoded));
  } catch (error) {
    console.error('Error decoding token:', error);
  }
};

// http://localhost:8000/app/api/tenant-saml-auth/?tenant_id=704916aa-fe73-452c-ad78-ead727949578
export const getTenantsSAMLConfig = async (tenant_id, onlyEnabled) => {
  const response = await axiosInstance.get(`/app/api/tenant-saml-auth/?tenant_id=${tenant_id}`);
  const results = response.data.results;

  const samlConfigs = results
    .filter(config => !onlyEnabled || config.enabled)
    .map(config => ({
      name: config.name,
      url: `${BACKEND_URL}/app/api/auth/saml/login-redirect/?tenant_id=${config.tenant_id}&auth_id=${config.id}`,
    }));

  return samlConfigs;
};

export const getTenantsConfig = async (tenant_id, onlyEnabled) => {
  const response = await axiosInstance.get(`/app/api/tenant-auth/?tenant_id=${tenant_id}`);
  const tenantUUID = response.data.tenant_id;
  const results = response.data.results;

  const oidcConfigs = results
    .filter(config => !onlyEnabled || config.enabled)
    .map(config => ({
      name: config.name,
      authority: config.oidc_issuer, // e.g. https://accounts.google.com or your Auth server
      client_id: config.oidc_client_id,
      redirect_uri: `${window.location.origin}${config.oidc_redirect_uri}`,
      response_type: "code",
      scope: "openid profile email",
      post_logout_redirect_uri: `${window.location.origin}/login`,
      // PKCE is enabled by default
    }));

  return {tenantID: tenantUUID, oidcConfigs: oidcConfigs};
};

export const login = async (email, password, tenant_id) => {
  try {
    const response = await axiosInstance.post(`/app/auth/token/`, {
      username: email,
      password: password
    });
    
    if (response.data && response.data.access) {
      console.log('[LOGIN] Storing selected_tenant_id in localStorage:', tenant_id);
      localStorage.setItem('selected_tenant_id', tenant_id);
      localStorage.setItem(TOKEN_KEY, response.data.access);
      localStorage.setItem(REFRESH_TOKEN, response.data.refresh);
      storeTokenPayload(response.data.access); // Decode and store payload
      return true;
    }
    return false;
  } catch (error) {
    console.error('Login error', error);
    throw error;
  }
};

export const loginWithExternalToken = async (external_token, access_token) => {
  try {
    const data = access_token ? { token: external_token, access_token: access_token } : { token: external_token };

    const response = await axiosInstance.post(`/app/auth/token/validate/`, data);
    
    if (response.data && response.data.access) {
      localStorage.setItem("LOGIN_PAGE", "/");
      localStorage.setItem(TOKEN_KEY, response.data.access);
      localStorage.setItem(REFRESH_TOKEN, response.data.refresh);
      storeTokenPayload(response.data.access); // Decode and store payload
      return true;
    }
    return false;
  } catch (error) {
    console.error('Login error', error);
    throw error;
  }
};


export const loginWithToken = async (access_token, refresh_token, tenant_id) => {
  try {
    
    if (access_token && refresh_token) {
      console.log('[LOGIN] Storing selected_tenant_id in localStorage:', tenant_id);
      localStorage.setItem('selected_tenant_id', tenant_id);
      localStorage.setItem("LOGIN_PAGE", `/app/${tenant_id}/dashboard`);
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(REFRESH_TOKEN, refresh_token);
      storeTokenPayload(access_token); // Decode and store payload
      return true;
    }
    return false;
  } catch (error) {
    console.error('Login error', error);
    throw error;
  }
};

export const logout = () => {
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_PAYLOAD_KEY); // Ensure token payload is also cleared
};

export const forgotPassword = async (email) => {
  try {
    const response = await axiosInstance.post(`/api/auth/forgot-password`, { email });
    return response.data;
  } catch (error) {
    console.error('Forgot password error', error);
    throw error;
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axiosInstance.post(`/api/auth/reset-password`, {
      token,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Reset password error', error);
    throw error;
  }
};

export const isAuthenticated = () => {
  return true;
  // const token = localStorage.getItem(TOKEN_KEY);
  // const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);

  // if (!token) {
  //   console.warn('isAuthenticated: No access token found in localStorage.');
  //   if (refreshTokenValue) {
  //     console.log('isAuthenticated: Attempting to refresh token using refresh token.');
  //     refreshToken().then((newAccessToken) => {
  //       if (newAccessToken) {
  //         console.log('isAuthenticated: Token refreshed successfully.');
  //       } else {
  //         console.warn('isAuthenticated: Failed to refresh token.');
  //       }
  //     }).catch((error) => {
  //       console.error('isAuthenticated: Error during token refresh.', error);
  //     });
  //   }
  //   return false;
  // }

  // try {
  //   const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
  //   const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  //   if (payload.exp && payload.exp < currentTime) {
  //     console.warn('isAuthenticated: Access token is expired.');
  //     localStorage.removeItem(TOKEN_KEY); // Clear expired token
  //     if (refreshTokenValue) {
  //       // console.log('isAuthenticated: Attempting to refresh token using refresh token.');
  //       // const response = axiosInstance.post(`${BACKEND_URL}/app/auth/token/refresh/`, {
  //       //   refresh: refreshToken,
  //       // });
  //       // const newAccessToken = response.data.access;
  //       // if (newAccessToken) {
  //       //   localStorage.setItem(TOKEN_KEY, newAccessToken);
  //       //   storeTokenPayload(newAccessToken);
  //       //   console.log('isAuthenticated: Token refreshed successfully.');
  //       // } else {
  //       //   console.warn('isAuthenticated: Failed to refresh token.');
  //       // }
      

  //       refreshToken().then((newAccessToken) => {
  //         if (newAccessToken) {
  //           console.log('isAuthenticated: Token refreshed successfully.');
  //         } else {
  //           console.warn('isAuthenticated: Failed to refresh token.');
  //         }
  //       }).catch((error) => {
  //         console.error('isAuthenticated: Error during token refresh.', error);
  //       });
  //     }
  //     return false;
  //   }
  //   return true;
  // } catch (error) {
  //   console.error('isAuthenticated: Invalid token format.', error);
  //   localStorage.removeItem(TOKEN_KEY); // Clear invalid token
  //   return false;
  // }
};

export const getToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    console.warn('getToken: No access token found in localStorage.');
  }
  return token;
};

export const refreshToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);

  if (!refreshToken) {
    console.warn('No refresh token available. Skipping token refresh.');
    return null;
  }

  try {
    const response = await axiosInstance.post(`/app/auth/token/refresh/`, {
      refresh: refreshToken,
    });

    if (response.data && response.data.access) {
      localStorage.setItem(TOKEN_KEY, response.data.access);
      storeTokenPayload(response.data.access); // Decode and store payload
      return response.data.access;
    }

    console.error('Unexpected response during token refresh:', response);
    return null;
  } catch (error) {
    if (error.response && error.response.status === 403) {
      console.error('Refresh token is invalid or expired. Logging out.');
      logout();
    } else {
      console.error('Error during token refresh:', error);
    }
    return null;
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add auth token to all axios requests with refresh capability
export const setupAxiosInterceptors = () => {
  // axios.interceptors.response.use(
  //   (response) => {
  //     return response;
  //   },
  //   async (error) => {
  //     const originalRequest = error.config;

  //     if (error.response?.status === 401 && !originalRequest._retry) {
  //       if (isRefreshing) {
  //         return new Promise(function(resolve, reject) {
  //           failedQueue.push({ resolve, reject });
  //         })
  //           .then(token => {
  //             originalRequest.headers['Authorization'] = 'Bearer ' + token;
  //             return axios(originalRequest);
  //           })
  //           .catch(err => {
  //             return Promise.reject(err);
  //           });
  //       }

  //       originalRequest._retry = true;
  //       isRefreshing = true;

  //       const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
  //       if (!refreshTokenValue) {
  //         console.log('No refresh token, logging out');
  //         logout();
  //         window.dispatchEvent(new Event('tokenChanged'));
  //         return Promise.reject(error);
  //       }

  //       try {
  //         const newAccessToken = await refreshToken();
  //         if (newAccessToken) {
  //           axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
  //           originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
  //           processQueue(null, newAccessToken);
  //           return axios(originalRequest);
  //         }
  //       } catch (refreshError) {
  //         processQueue(refreshError, null);
  //         console.error('Token refresh failed:', refreshError);
  //         logout();
  //         window.dispatchEvent(new Event('tokenChanged'));
  //         return Promise.reject(refreshError);
  //       } finally {
  //         isRefreshing = false;
  //       }
  //     }

  //     return Promise.reject(error);
  //   }
  // );

  // axios.interceptors.request.use(
  //   async (config) => {
  //     const token = getToken();
  //     if (token) {
  //       config.headers['Authorization'] = `Bearer ${token}`;
  //     }
  //     return config;
  //   },
  //   (error) => {
  //     return Promise.reject(error);
  //   }
  // );
};

